/**
 * Single FDA enforcement-letter row for the letter search results list.
 *
 * Hybrid click-through routing (header is the navigation target):
 *   - linked letter   (``letter.drug_id != null``):
 *       ``/drug/{drug_id}?source=fda-letter&version={letter_id}``
 *   - unlinked letter (``letter.drug_id`` null/undefined):
 *       ``/fda-letter/{letter_id}``
 *
 * Evidence rendering (Cogni AI letter path only) is three-tier:
 *   1. ``evidence`` with a span  → relevant-part window, sentence
 *      <mark>-highlighted, "Show full excerpt" expands the full chunk.
 *   2. ``evidence`` without span → cited chunk, 2-line clamp, no
 *      highlight, "Show full excerpt" expands it.
 *   3. no ``evidence``           → ``best_snippet``, 2-line clamp.
 */
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LetterKeywordResultItem, LetterEvidence } from '../types';

export interface LetterRowClick {
  target: string;
  letter_id: number;
  drug_id: number | null | undefined;
}

interface Props {
  letter: LetterKeywordResultItem;
  /** Optional click interceptor. When undefined the row navigates
   *  directly via ``useNavigate``. */
  onClick?: (click: LetterRowClick) => void;
}

/** Characters of context shown on each side of the highlighted sentence
 *  in the tier-1 collapsed view. */
const CONTEXT_CHARS = 120;

/** Compute the click-through target URL. Exported only for tests. */
export function letterTarget(letter: LetterKeywordResultItem): string {
  return letter.drug_id != null
    ? `/drug/${letter.drug_id}?source=fda-letter&version=${letter.letter_id}`
    : `/fda-letter/${letter.letter_id}`;
}

/** Compute the click-through target URL for an individual drug reference
 *  on a letter row.  Mirrors ``letterTarget`` but pivots on ``ref.drug_id``
 *  instead of the row's primary drug id, so role-tagged chips route to
 *  their own drug page rather than the letter's "lead" drug. */
function refTarget(letterId: number, drugId: number): string {
  return `/drug/${drugId}?source=fda-letter&version=${letterId}`;
}

/** Per-role chip styling (Target = indigo, Comparator = amber, Mentioned
 *  = slate).  Keeps colour mapping in one place so chips stay consistent
 *  with the rest of the row's Tailwind palette. */
const ROLE_BADGE_CLASS: Record<'target' | 'comparator' | 'mentioned', string> = {
  target: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  comparator: 'bg-amber-50 text-amber-700 border-amber-200',
  mentioned: 'bg-slate-50 text-slate-600 border-slate-200',
};

const ROLE_LABEL: Record<'target' | 'comparator' | 'mentioned', string> = {
  target: 'Target',
  comparator: 'Comparator',
  mentioned: 'Mentioned',
};

/** Render ``text`` with ``[start, end)`` wrapped in a <mark>. */
function withHighlight(
  text: string,
  start: number,
  end: number,
): React.ReactNode {
  return (
    <>
      {text.slice(0, start)}
      <mark className="bg-amber-100 text-slate-900 rounded px-0.5">
        {text.slice(start, end)}
      </mark>
      {text.slice(end)}
    </>
  );
}

/** Three-tier evidence block. Collapsed by default; expands to the full
 *  cited chunk. */
const EvidenceBlock: React.FC<{ evidence: LetterEvidence }> = ({
  evidence,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { chunk_text, span_start, span_end } = evidence;
  const hasSpan =
    span_start != null && span_end != null && span_end > span_start;

  let body: React.ReactNode;
  if (expanded) {
    body = hasSpan
      ? withHighlight(chunk_text, span_start as number, span_end as number)
      : chunk_text;
  } else if (hasSpan) {
    const from = Math.max(0, (span_start as number) - CONTEXT_CHARS);
    const to = Math.min(
      chunk_text.length,
      (span_end as number) + CONTEXT_CHARS,
    );
    const slice = chunk_text.slice(from, to);
    body = (
      <>
        {from > 0 && '…'}
        {withHighlight(
          slice,
          (span_start as number) - from,
          (span_end as number) - from,
        )}
        {to < chunk_text.length && '…'}
      </>
    );
  } else {
    body = chunk_text;
  }

  // Clamp only when collapsed AND there is no span (tier 2 — full chunk).
  const clamp = !expanded && !hasSpan ? 'line-clamp-2' : undefined;

  return (
    <div className="mt-1.5">
      <p className={['text-sm text-slate-700', clamp].filter(Boolean).join(' ')}>
        {body}
      </p>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
        className="mt-1 text-xs font-medium text-violet-600 hover:text-violet-700"
      >
        {expanded ? 'Show less' : 'Show full excerpt'}
      </button>
    </div>
  );
};

export const LetterResultRow: React.FC<Props> = ({ letter, onClick }) => {
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    const target = letterTarget(letter);
    const click: LetterRowClick = {
      target,
      letter_id: letter.letter_id,
      drug_id: letter.drug_id ?? null,
    };
    if (onClick) {
      onClick(click);
    } else {
      navigate(target);
    }
  }, [letter, onClick, navigate]);

  const badgeClass =
    letter.letter_type === 'warning'
      ? 'bg-red-50 text-red-700 border-red-200'
      : letter.letter_type === 'promotional'
      ? 'bg-violet-50 text-violet-700 border-violet-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';

  const title = letter.letter_title || `FDA Enforcement Letter #${letter.letter_id}`;

  // Role-tagged drug chips. ``matched_references`` is the canonical
  // source: one chip per entry, role-coloured. An empty list means no
  // chips — this is a legitimate current state for letters surfaced
  // via the Unclassified Type-filter pill (no fda_letter_drugs row).
  // The chip set is "exactly the drugs that justified inclusion" per
  // the spec; falling back to the primary ``drug_id``/``drug_name``
  // would muddy the Type-filter semantics. The primary FK is still
  // used by ``letterTarget`` for the row-level clickthrough — just
  // not for the chip row.
  const refs = letter.matched_references ?? [];
  const showRefChips = refs.length > 0;

  return (
    <div className="w-full bg-white rounded-xl border border-slate-200/60 hover:border-violet-200 hover:shadow-md transition-all duration-200 px-4 py-3">
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left flex items-start gap-3"
      >
        <span
          className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded border ${badgeClass}`}
        >
          {letter.letter_type}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">
            {title}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {letter.recipient && <span>{letter.recipient}</span>}
            {letter.recipient && letter.issued_date && <span> · </span>}
            {letter.issued_date && <span>{letter.issued_date}</span>}
          </p>
        </div>
      </button>
      {showRefChips && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {refs.map((ref) => {
            const chipClick: LetterRowClick = {
              target: refTarget(letter.letter_id, ref.drug_id),
              letter_id: letter.letter_id,
              drug_id: ref.drug_id,
            };
            return (
              <button
                key={`${ref.drug_id}-${ref.role}`}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onClick) onClick(chipClick);
                  else navigate(chipClick.target);
                }}
                className={`px-2 py-0.5 text-xs font-medium rounded-full border ${ROLE_BADGE_CLASS[ref.role]} hover:shadow-sm transition-all duration-150`}
              >
                <span>{ref.drug_name}</span>
                <span className="ml-1.5 text-[10px] uppercase tracking-wide opacity-75">
                  {ROLE_LABEL[ref.role]}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {letter.evidence ? (
        <EvidenceBlock evidence={letter.evidence} />
      ) : (
        letter.best_snippet && (
          <p className="text-sm text-slate-700 mt-1.5 line-clamp-2">
            {letter.best_snippet}
          </p>
        )
      )}
    </div>
  );
};

export default LetterResultRow;
